import React, { useCallback } from 'react';
import { useIdentityContext } from '@design-stack-vista/identity-provider';
import { UploadDropZone, UploadsButton } from '@design-stack-vista/upload-components';
import { AlertBox, AlertBoxDismissButton, Box, FlexBox, Spinner, Typography } from '@vp/swan';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useUploadContext } from 'src/hooks/UploadContextHooks';
import { useIsPdfOnly, useValidationContext } from 'src/hooks/ValidationContextHooks';
import { UPLOAD_MAX_SIZE_MB } from 'src/utils/Constants';
import { text } from 'src/utils/localizationUtil';
import { UploadProperties } from './UploadProperties';
import { UploadStatus } from './UploadStatus';
import './sherbertUploader.css';
import { useFileUpload } from './useFileUpload';

const pdfFileTypesText = ['pdf'];
const allFileTypesText = ['docx', 'jpg', 'png', 'gif', 'bmp', ...pdfFileTypesText];

export const SherbertUploader = () => {
  const {
    uploadRestrictionFailed,
    setUploadRestrictionFailed,
    setUploadMethod,
    upload,
    isUploading,
    fileSize,
  } = useUploadContext();
  const { isValidating } = useValidationContext();
  const { productInformation } = useProductContext();
  const { auth: vpAuth } = useIdentityContext();
  const isPdfOnly = useIsPdfOnly();
  const { handleUploadStart, handleBeforeUpload } = useFileUpload();

  const allowedFileTypesText = isPdfOnly ? pdfFileTypesText : allFileTypesText;
  const handleBeforeDragAndDrop = useCallback(() => {
    setUploadMethod('drag_and_drop');
  }, [setUploadMethod]);

  return (
    <Box mb="7">
      {!upload && !isUploading && !isValidating ? (
        vpAuth ? (
          <>
            <UploadDropZone
              noClick={true}
              noKeyboard={true}
              onBeforeUpload={handleBeforeDragAndDrop}
              onUploadStart={handleUploadStart}
            >
              <UploadsButton
                onBeforeUpload={handleBeforeUpload}
                onUploadStart={handleUploadStart}
                className="upload-button"
                width="full-width"
              >
                {text('upload-label')}
                <Typography as="span" fontWeight="normal" fontSize="small" textColor="subtle">
                  {text('validations.file size.title', { maxFileSizeMegabytes: UPLOAD_MAX_SIZE_MB })}
                </Typography>
              </UploadsButton>
            </UploadDropZone>
            <AlertBox
              skin="error"
              width="narrow"
              dismissed={!uploadRestrictionFailed}
              mt="7"
              onRequestDismiss={() => {
                setUploadRestrictionFailed(false);
              }}
            >
              {text('upload-drag-reject', { acceptedFileTypes: allowedFileTypesText, maxMB: UPLOAD_MAX_SIZE_MB })}
              <AlertBoxDismissButton visuallyHiddenLabel={text('dismiss-alert')} />
            </AlertBox>
          </>
        ) : (
          <FlexBox justifyContent="center" alignItems="center">
            <Spinner accessibleText={text('loading')} size="standard" />
          </FlexBox>
        )
      ) : isUploading || isValidating ? (
        <UploadStatus isValidating={isValidating} isUploading={isUploading} fileSize={fileSize} />
      ) : (
        upload && (
          <UploadProperties onUploadStart={handleUploadStart} productInformation={productInformation} upload={upload} />
        )
      )}
    </Box>
  );
};
